import React, {
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import ContextualMenu from '../ContextualMenu/ContextualMenu.component'
import type { ContextualMenuItem } from '../ContextualMenu/ContextualMenu.types'
import { MailboxNavIcons } from './MailboxNav.icons'
import type {
  MailboxNavColorOption,
  MailboxNavCreatePayload,
  MailboxNavGroup,
  MailboxNavItem,
  MailboxNavProps,
  MailboxNavSection,
  MailboxNavUpdatePayload,
} from './MailboxNav.types'
import {
  MAILBOX_NAV_EMAIL_GROUPS,
  MAILBOX_NAV_EMAIL_SECTIONS,
  MAILBOX_NAV_FAX_GROUPS,
  MAILBOX_NAV_FAX_SECTIONS,
  MAILBOX_NAV_LABEL_COLORS,
} from './MailboxNav.types'
import './MailboxNav.css'

const EDIT_MENU_IDS = new Set(['rename', 'edit'])
const DELETE_MENU_IDS = new Set(['delete'])

function itemLabelText(label: MailboxNavItem['label']): string {
  return typeof label === 'string' ? label : ''
}

function iconStyle(color?: string): CSSProperties | undefined {
  if (!color) {
    return undefined
  }
  return {
    color,
    ['--ds-mailbox-nav-icon-color' as string]: color,
  }
}

function renderNavIcon(icon: ReactNode, color?: string): ReactNode {
  if (!icon) {
    return null
  }

  return (
    <span
      className={`ds-mailbox-nav__icon${
        color ? ' ds-mailbox-nav__icon--colored' : ''
      }`}
      style={iconStyle(color)}
      aria-hidden="true"
    >
      {icon}
    </span>
  )
}

type ItemEditorProps = {
  mode: 'create' | 'edit'
  kind: 'folder' | 'label'
  icon?: ReactNode
  placeholder?: string
  initialName?: string
  initialColor?: string
  colorOptions: MailboxNavColorOption[]
  isPreview?: boolean
  onSave: (name: string, color?: string) => void
  onCancel: () => void
}

const ItemEditor: React.FC<ItemEditorProps> = ({
  mode,
  kind,
  icon,
  placeholder,
  initialName = '',
  initialColor,
  colorOptions,
  isPreview = false,
  onSave,
  onCancel,
}) => {
  const defaultColor = colorOptions[0]?.color
  const [name, setName] = useState(initialName)
  const [color, setColor] = useState(initialColor ?? defaultColor)

  useEffect(() => {
    setName(initialName)
    setColor(initialColor ?? defaultColor)
  }, [initialName, initialColor, defaultColor])

  const canSave = name.trim().length > 0
  const showColors = kind === 'label' && colorOptions.length > 0

  const submit = (): void => {
    if (!canSave || isPreview) {
      return
    }
    onSave(name.trim(), showColors ? color : undefined)
  }

  return (
    <div className="ds-mailbox-nav__editor">
      <div className="ds-mailbox-nav__editor-row">
        <div className="ds-mailbox-nav__editor-input-wrap">
          {renderNavIcon(icon, showColors ? color : undefined)}
          <input
            className="ds-mailbox-nav__editor-input"
            type="text"
            value={name}
            placeholder={placeholder ?? (mode === 'create' ? 'Name' : 'Rename')}
            aria-label={placeholder ?? (mode === 'create' ? 'Name' : 'Rename')}
            readOnly={isPreview}
            tabIndex={isPreview ? -1 : undefined}
            autoFocus={!isPreview}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                submit()
              }
              if (event.key === 'Escape') {
                event.preventDefault()
                onCancel()
              }
            }}
          />
        </div>
        <div className="ds-mailbox-nav__editor-actions">
          <button
            type="button"
            className="ds-mailbox-nav__editor-cancel"
            aria-label="Cancel"
            tabIndex={isPreview ? -1 : undefined}
            onClick={onCancel}
          >
            {MailboxNavIcons.close()}
          </button>
          <button
            type="button"
            className={`ds-mailbox-nav__editor-save${
              !canSave ? ' ds-mailbox-nav__editor-save--disabled' : ''
            }`}
            aria-label="Save"
            tabIndex={isPreview ? -1 : undefined}
            disabled={!canSave || isPreview}
            onClick={submit}
          >
            {MailboxNavIcons.save()}
          </button>
        </div>
      </div>
      {showColors ? (
        <div className="ds-mailbox-nav__editor-colors">
          <span className="ds-mailbox-nav__editor-colors-label">
            Select color
          </span>
          <div className="ds-mailbox-nav__editor-swatches" role="list">
            {colorOptions.map((option) => {
              const active = option.color === color
              return (
                <button
                  key={option.id}
                  type="button"
                  role="listitem"
                  className={`ds-mailbox-nav__editor-swatch${
                    active ? ' ds-mailbox-nav__editor-swatch--active' : ''
                  }`}
                  style={{ color: option.color }}
                  aria-label={option.label ?? option.id}
                  aria-pressed={active}
                  tabIndex={isPreview ? -1 : undefined}
                  onClick={() => {
                    if (!isPreview) {
                      setColor(option.color)
                    }
                  }}
                >
                  {MailboxNavIcons.swatch()}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

type NavRowProps = {
  item: MailboxNavItem
  selected: boolean
  previewHover?: boolean
  forceMenuVisible?: boolean
  isPreview: boolean
  darkMode?: boolean
  onSelect: (id: string) => void
  onMenuSelect: (itemId: string, menuId: string) => void
  nested?: boolean
}

const NavRow: React.FC<NavRowProps> = ({
  item,
  selected,
  previewHover,
  forceMenuVisible,
  isPreview,
  darkMode = false,
  onSelect,
  onMenuSelect,
  nested = false,
}) => {
  const [hovered, setHovered] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const showMenu =
    (item.menuItems?.length ?? 0) > 0 &&
    (forceMenuVisible || hovered || menuOpen)

  const showCount =
    item.count != null &&
    item.count > 0 &&
    !(showMenu && (item.menuItems?.length ?? 0) > 0)

  const rowClass = [
    'ds-mailbox-nav__row',
    nested ? 'ds-mailbox-nav__row--nested' : '',
    selected ? 'ds-mailbox-nav__row--selected' : '',
    previewHover ? 'ds-mailbox-nav__row--preview-hover' : '',
    item.disabled ? 'ds-mailbox-nav__row--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const countClass = [
    'ds-mailbox-nav__count',
    item.hasUnread ? 'ds-mailbox-nav__count--unread' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const menuItems: ContextualMenuItem[] = (item.menuItems ?? []).map(
    (action) => ({
      id: action.id,
      label: action.label,
      icon: action.icon,
      onSelect: () => onMenuSelect(item.id, action.id),
    }),
  )

  return (
    <div
      className={rowClass}
      onMouseEnter={() => {
        if (!isPreview) {
          setHovered(true)
        }
      }}
      onMouseLeave={() => {
        if (!isPreview && !menuOpen) {
          setHovered(false)
        }
      }}
    >
      <button
        type="button"
        className="ds-mailbox-nav__row-button"
        disabled={item.disabled}
        tabIndex={isPreview ? -1 : undefined}
        aria-current={selected ? 'page' : undefined}
        onClick={() => {
          if (!isPreview && !item.disabled) {
            onSelect(item.id)
          }
        }}
      >
        <span className="ds-mailbox-nav__row-main">
          {renderNavIcon(item.icon, item.color)}
          <span className="ds-mailbox-nav__label">{item.label}</span>
        </span>
        {showCount ? <span className={countClass}>{item.count}</span> : null}
      </button>
      {showMenu ? (
        <div
          className="ds-mailbox-nav__menu"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <ContextualMenu
            ariaLabel={`Actions for ${itemLabelText(item.label) || 'item'}`}
            items={menuItems}
            darkMode={darkMode}
            open={isPreview ? forceMenuVisible : menuOpen}
            onOpenChange={(open) => {
              if (isPreview) {
                return
              }
              setMenuOpen(open)
              if (!open) {
                setHovered(false)
              }
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

type SectionBlockProps = {
  section: MailboxNavSection
  selectedId?: string
  isPreview: boolean
  darkMode?: boolean
  forceCollapsed?: boolean
  forceCreating?: boolean
  forceMenuItemId?: string
  previewHoverId?: string
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  onSelect: (id: string) => void
  onItemMenuSelect?: (itemId: string, menuId: string) => void
  onCreate?: (payload: MailboxNavCreatePayload) => void
  onUpdate?: (payload: MailboxNavUpdatePayload) => void
  onDelete?: (sectionId: string, itemId: string) => void
}

const SectionBlock: React.FC<SectionBlockProps> = ({
  section,
  selectedId,
  isPreview,
  darkMode = false,
  forceCollapsed,
  forceCreating,
  forceMenuItemId,
  previewHoverId,
  canCreate,
  canUpdate,
  canDelete,
  onSelect,
  onItemMenuSelect,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const isOpenControlled = section.open !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    section.defaultOpen ?? true,
  )
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)

  const open = forceCollapsed
    ? false
    : isOpenControlled
      ? (section.open as boolean)
      : uncontrolledOpen

  const showCreating = forceCreating || creating
  const kind = section.kind ?? 'folder'
  const colorOptions = section.colorOptions ?? MAILBOX_NAV_LABEL_COLORS
  const itemIcon =
    section.itemIcon ??
    (kind === 'label' ? MailboxNavIcons.tag() : MailboxNavIcons.folder())

  const ensureOpen = (): void => {
    if (!open) {
      if (!isOpenControlled) {
        setUncontrolledOpen(true)
      }
      section.onOpenChange?.(true)
    }
  }

  const toggleOpen = (): void => {
    if (isPreview) {
      return
    }
    const next = !open
    if (!isOpenControlled) {
      setUncontrolledOpen(next)
    }
    section.onOpenChange?.(next)
  }

  const startCreate = (): void => {
    if (isPreview) {
      return
    }
    ensureOpen()
    setEditingId(null)
    setCreating(true)
    section.onAdd?.()
  }

  const cancelEditor = (): void => {
    setCreating(false)
    setEditingId(null)
  }

  const handleMenuSelect = (itemId: string, menuId: string): void => {
    if (EDIT_MENU_IDS.has(menuId) && canUpdate) {
      setCreating(false)
      setEditingId(itemId)
      return
    }

    if (DELETE_MENU_IDS.has(menuId) && canDelete) {
      onDelete?.(section.id, itemId)
      return
    }

    onItemMenuSelect?.(itemId, menuId)
  }

  const showAddButton = canCreate || section.onAdd != null || isPreview

  return (
    <div className="ds-mailbox-nav__section">
      <div className="ds-mailbox-nav__section-header">
        <button
          type="button"
          className="ds-mailbox-nav__section-toggle"
          tabIndex={isPreview ? -1 : undefined}
          aria-expanded={open}
          onClick={toggleOpen}
        >
          <span className="ds-mailbox-nav__section-title">
            {renderNavIcon(section.icon)}
            <span>{section.title}</span>
          </span>
          <span
            className={`ds-mailbox-nav__chevron${
              open ? '' : ' ds-mailbox-nav__chevron--collapsed'
            }`}
            aria-hidden="true"
          >
            {MailboxNavIcons.chevron()}
          </span>
        </button>
        {showAddButton ? (
          <button
            type="button"
            className="ds-mailbox-nav__add"
            aria-label={section.addAriaLabel ?? `Add ${String(section.title)}`}
            tabIndex={isPreview ? -1 : undefined}
            onClick={(event) => {
              event.stopPropagation()
              if (isPreview) {
                return
              }
              if (canCreate) {
                startCreate()
                return
              }
              section.onAdd?.()
            }}
          >
            {MailboxNavIcons.add()}
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="ds-mailbox-nav__section-body">
          {showCreating ? (
            <ItemEditor
              mode="create"
              kind={kind}
              icon={itemIcon}
              placeholder={section.createPlaceholder}
              colorOptions={colorOptions}
              isPreview={isPreview}
              onCancel={cancelEditor}
              onSave={(name, color) => {
                onCreate?.({ sectionId: section.id, name, color })
                cancelEditor()
              }}
            />
          ) : null}
          {section.items.map((item) => {
            if (editingId === item.id) {
              return (
                <ItemEditor
                  key={item.id}
                  mode="edit"
                  kind={kind}
                  icon={item.icon ?? itemIcon}
                  placeholder={kind === 'label' ? 'Label name' : 'Folder name'}
                  initialName={itemLabelText(item.label)}
                  initialColor={item.color}
                  colorOptions={colorOptions}
                  onCancel={cancelEditor}
                  onSave={(name, color) => {
                    onUpdate?.({
                      sectionId: section.id,
                      itemId: item.id,
                      name,
                      color,
                    })
                    cancelEditor()
                  }}
                />
              )
            }

            return (
              <NavRow
                key={item.id}
                item={item}
                nested
                selected={item.id === selectedId}
                previewHover={previewHoverId === item.id}
                forceMenuVisible={forceMenuItemId === item.id}
                isPreview={isPreview}
                darkMode={darkMode}
                onSelect={onSelect}
                onMenuSelect={handleMenuSelect}
              />
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

const MailboxNav: React.FC<MailboxNavProps> = ({
  groups,
  sections,
  value,
  defaultValue,
  onChange,
  onItemMenuSelect,
  onCreate,
  onUpdate,
  onDelete,
  width = 240,
  className = '',
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null
  const isControlled = value !== undefined
  const canCreate = onCreate != null
  const canUpdate = onUpdate != null
  const canDelete = onDelete != null

  const useEmailSample = previewState === 'email-sample'

  const resolvedGroups: MailboxNavGroup[] =
    groups ??
    (isPreview
      ? useEmailSample
        ? MAILBOX_NAV_EMAIL_GROUPS
        : MAILBOX_NAV_FAX_GROUPS
      : [])

  const resolvedSections: MailboxNavSection[] =
    sections ??
    (isPreview
      ? useEmailSample
        ? MAILBOX_NAV_EMAIL_SECTIONS
        : MAILBOX_NAV_FAX_SECTIONS
      : [])

  const [uncontrolledValue, setUncontrolledValue] = useState<
    string | undefined
  >(defaultValue ?? resolvedGroups[0]?.items[0]?.id ?? 'inbox')

  const selectedId = (() => {
    if (isPreview) {
      switch (previewState) {
        case 'selected':
          return 'sent'
        case 'hover':
          return 'inbox'
        case 'with-overflow':
          return 'folder-claims'
        case 'creating-label':
        case 'email-sample':
        case 'collapsed':
        case 'default':
        default:
          return 'inbox'
      }
    }
    return isControlled ? value : uncontrolledValue
  })()

  const previewHoverId = previewState === 'hover' ? 'draft' : undefined
  const forceMenuItemId =
    previewState === 'with-overflow' ? 'folder-claims' : undefined
  const forceCollapsed = previewState === 'collapsed'
  const forceCreatingSectionId =
    previewState === 'creating-label' ? 'labels' : undefined

  const handleSelect = (id: string): void => {
    if (isPreview) {
      return
    }
    if (!isControlled) {
      setUncontrolledValue(id)
    }
    onChange?.(id)
  }

  const classNames = [
    'ds-mailbox-nav',
    darkMode ? 'ds-mailbox-nav--dark' : '',
    isPreview ? 'ds-mailbox-nav--preview' : '',
    previewState ? `ds-mailbox-nav--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const styleWidth = typeof width === 'number' ? `${width}px` : width

  return (
    <nav
      className={classNames}
      style={{ width: styleWidth }}
      aria-label="Mailbox navigation"
    >
      <div className="ds-mailbox-nav__scroll">
        {resolvedGroups.map((group, groupIndex) => (
          <React.Fragment key={group.id}>
            <div className="ds-mailbox-nav__group">
              {group.items.map((item) => (
                <NavRow
                  key={item.id}
                  item={item}
                  selected={item.id === selectedId}
                  previewHover={previewHoverId === item.id}
                  isPreview={isPreview}
                  darkMode={darkMode}
                  onSelect={handleSelect}
                  onMenuSelect={(itemId, menuId) =>
                    onItemMenuSelect?.(itemId, menuId)
                  }
                />
              ))}
            </div>
            {groupIndex < resolvedGroups.length - 1 ||
            resolvedSections.length > 0 ? (
              <div className="ds-mailbox-nav__divider" />
            ) : null}
          </React.Fragment>
        ))}

        {resolvedSections.map((section, sectionIndex) => (
          <React.Fragment key={section.id}>
            <SectionBlock
              section={section}
              selectedId={selectedId}
              isPreview={isPreview}
              darkMode={darkMode}
              forceCollapsed={forceCollapsed}
              forceCreating={forceCreatingSectionId === section.id}
              forceMenuItemId={forceMenuItemId}
              previewHoverId={previewHoverId}
              canCreate={canCreate}
              canUpdate={canUpdate}
              canDelete={canDelete}
              onSelect={handleSelect}
              onItemMenuSelect={onItemMenuSelect}
              onCreate={onCreate}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
            {sectionIndex < resolvedSections.length - 1 ? (
              <div className="ds-mailbox-nav__divider" />
            ) : null}
          </React.Fragment>
        ))}
      </div>
    </nav>
  )
}

export default MailboxNav
